import { useState, useEffect, useCallback } from "react";
import { Loader2, Send, Eye, Plus, RotateCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { toast } from "sonner";
import { apiClient, formatApiErrorDetail } from "@/lib/api";
import { AdminGuard } from "@/components/admin/AdminShell";
import { formatDateTime } from "@/constants/business_status";

function Badge({ children }) {
  return <span className="text-xs font-semibold bg-navy/5 text-navy rounded-full px-2.5 py-1 whitespace-nowrap">{children}</span>;
}

export function CommunicationsPage() {
  const [tab, setTab] = useState("templates");
  const [templates, setTemplates] = useState(null);
  const [segments, setSegments] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [history, setHistory] = useState([]);
  const [preview, setPreview] = useState(null);
  const [editing, setEditing] = useState(null);
  const [campForm, setCampForm] = useState({ name: "", template_id: "", segment: "tous" });

  const load = useCallback(() => {
    apiClient.get("/admin/email-templates").then((r) => setTemplates(r.data)).catch(() => {});
    apiClient.get("/admin/segments").then((r) => setSegments(r.data.items)).catch(() => {});
    apiClient.get("/admin/campaigns").then((r) => setCampaigns(r.data.items)).catch(() => {});
    apiClient.get("/admin/email-history", { params: { page_size: 50 } }).then((r) => setHistory(r.data.items)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const showPreview = async (id) => {
    const r = await apiClient.get(`/admin/email-templates/${id}/preview`);
    setPreview(r.data);
  };
  const testSend = async (id) => {
    const to = window.prompt("Envoyer un e-mail de test à :");
    if (!to) return;
    await apiClient.post(`/admin/email-templates/${id}/test-send`, { to_email: to });
    toast.success("E-mail de test en file d'envoi");
  };
  const saveTemplate = async () => {
    try {
      await apiClient.patch(`/admin/email-templates/${editing.id}`, {
        name: editing.name, category: editing.category, subject: editing.subject, html: editing.html });
      toast.success("Modèle enregistré"); setEditing(null); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const createCampaign = async () => {
    if (!campForm.name || !campForm.template_id) return toast.error("Nom et modèle requis");
    try { await apiClient.post("/admin/campaigns", campForm); toast.success("Campagne créée"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const sendCampaign = async (id) => {
    if (!window.confirm("Envoyer cette campagne maintenant ?")) return;
    try { const r = await apiClient.post(`/admin/campaigns/${id}/send`); toast.success(`${r.data.sent} e-mail(s) envoyés`); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <AdminGuard>
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <h1 className="font-heading font-extrabold text-2xl text-navy mr-auto">Communications</h1>
        {[["templates", "Modèles"], ["campaigns", "Campagnes"], ["history", "Historique"]].map(([k, l]) => (
          <button key={k} data-testid={`comms-tab-${k}`} onClick={() => setTab(k)}
            className={`px-3 py-1.5 rounded-full text-sm font-semibold ${tab === k ? "bg-navy text-white" : "bg-white text-navy"}`}>{l}</button>
        ))}
      </div>

      {tab === "templates" && (
        !templates ? <Loader2 className="w-6 h-6 animate-spin mx-auto text-navy mt-10" /> : editing ? (
          <div className="bg-white rounded-2xl shadow-sm p-5 max-w-3xl" data-testid="template-editor">
            <h2 className="font-bold text-navy mb-3">Modifier : {editing.name}</h2>
            <label className="text-xs font-semibold text-navy">Nom</label>
            <Input data-testid="template-name" value={editing.name} onChange={(e) => setEditing((p) => ({ ...p, name: e.target.value }))} className="mb-2 bg-white" />
            <label className="text-xs font-semibold text-navy">Sujet</label>
            <Input data-testid="template-subject" value={editing.subject} onChange={(e) => setEditing((p) => ({ ...p, subject: e.target.value }))} className="mb-2 bg-white" />
            <label className="text-xs font-semibold text-navy">Contenu HTML (variables : {"{{customer_first_name}}, {{case_number}}, {{quote_number}}, {{client_portal_url}}…"})</label>
            <Textarea data-testid="template-html" value={editing.html} onChange={(e) => setEditing((p) => ({ ...p, html: e.target.value }))} rows={12} className="mb-3 bg-white font-mono text-xs" />
            <div className="flex gap-2">
              <Button data-testid="template-save" onClick={saveTemplate} className="bg-navy text-white rounded-lg">Enregistrer</Button>
              <Button variant="outline" className="rounded-lg" onClick={() => setEditing(null)}>Annuler</Button>
            </div>
          </div>
        ) : (
          <div className="bg-white rounded-2xl shadow-sm divide-y" data-testid="templates-list">
            {templates.items.map((t) => (
              <div key={t.id} className="flex flex-wrap items-center gap-2 px-4 py-3" data-testid={`template-row-${t.code}`}>
                <div className="mr-auto"><div className="text-sm font-semibold text-navy">{t.name}</div>
                  <div className="text-xs text-muted-foreground">{t.subject}</div></div>
                <Badge>{t.category}</Badge>
                <Button size="sm" variant="ghost" className="text-xs h-8" data-testid={`template-preview-${t.code}`} onClick={() => showPreview(t.id)}><Eye className="w-3.5 h-3.5 mr-1" />Aperçu</Button>
                <Button size="sm" variant="ghost" className="text-xs h-8" data-testid={`template-edit-${t.code}`} onClick={() => setEditing(t)}>Modifier</Button>
                <Button size="sm" variant="ghost" className="text-xs h-8" data-testid={`template-test-${t.code}`} onClick={() => testSend(t.id)}><Send className="w-3.5 h-3.5 mr-1" />Test</Button>
              </div>
            ))}
          </div>
        )
      )}

      {preview && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => setPreview(null)}>
          <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[85vh] overflow-y-auto p-5" onClick={(e) => e.stopPropagation()} data-testid="template-preview-modal">
            <div className="font-bold text-navy mb-2">{preview.subject}</div>
            <div dangerouslySetInnerHTML={{ __html: preview.html }} />
            <Button className="mt-4 rounded-lg" variant="outline" onClick={() => setPreview(null)}>Fermer</Button>
          </div>
        </div>
      )}

      {tab === "campaigns" && (
        <>
          <div className="bg-white rounded-2xl shadow-sm p-4 mb-4 flex flex-wrap gap-2 items-end" data-testid="campaign-form">
            <div><label className="text-[11px] font-semibold text-navy">Nom</label>
              <Input data-testid="campaign-name" value={campForm.name} onChange={(e) => setCampForm((p) => ({ ...p, name: e.target.value }))} className="h-9 bg-white rounded-lg" /></div>
            <div className="min-w-[200px]"><label className="text-[11px] font-semibold text-navy">Modèle</label>
              <Select value={campForm.template_id} onValueChange={(v) => setCampForm((p) => ({ ...p, template_id: v }))}>
                <SelectTrigger data-testid="campaign-template" className="h-9 bg-white rounded-lg"><SelectValue placeholder="Choisir" /></SelectTrigger>
                <SelectContent>{(templates?.items || []).map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent>
              </Select></div>
            <div className="min-w-[220px]"><label className="text-[11px] font-semibold text-navy">Segment</label>
              <Select value={campForm.segment} onValueChange={(v) => setCampForm((p) => ({ ...p, segment: v }))}>
                <SelectTrigger data-testid="campaign-segment" className="h-9 bg-white rounded-lg"><SelectValue /></SelectTrigger>
                <SelectContent>{segments.map((s) => <SelectItem key={s.code} value={s.code}>{s.label} ({s.count_with_email})</SelectItem>)}</SelectContent>
              </Select></div>
            <Button data-testid="campaign-create" onClick={createCampaign} className="bg-orange hover:bg-orange/90 text-white rounded-lg h-9"><Plus className="w-4 h-4 mr-1" />Créer</Button>
          </div>
          <div className="bg-white rounded-2xl shadow-sm divide-y" data-testid="campaigns-list">
            {campaigns.length === 0 && <div className="py-8 text-center text-muted-foreground text-sm">Aucune campagne</div>}
            {campaigns.map((c) => (
              <div key={c.id} className="flex items-center gap-3 px-4 py-3" data-testid={`campaign-row-${c.id}`}>
                <div className="mr-auto"><div className="text-sm font-semibold text-navy">{c.name}</div>
                  <div className="text-xs text-muted-foreground">Segment : {c.segment} • {c.sent_count} envoyés</div></div>
                <Badge>{c.status}</Badge>
                {c.status !== "sent" && <Button data-testid={`campaign-send-${c.id}`} size="sm" className="bg-navy text-white rounded-lg text-xs h-8" onClick={() => sendCampaign(c.id)}><Send className="w-3.5 h-3.5 mr-1" />Envoyer</Button>}
              </div>
            ))}
          </div>
        </>
      )}

      {tab === "history" && (
        <div className="bg-white rounded-2xl shadow-sm divide-y" data-testid="email-history-list">
          {history.length === 0 && <div className="py-8 text-center text-muted-foreground text-sm">Aucun e-mail envoyé</div>}
          {history.map((h) => (
            <div key={h.id} className="flex items-center gap-3 px-4 py-2.5 text-sm">
              <span className="font-semibold text-navy w-52 truncate">{h.to}</span>
              <span className="flex-1 truncate text-xs">{h.subject}</span>
              <Badge>{h.status}</Badge>
              <span className="text-xs text-muted-foreground">{formatDateTime(h.created_at)}</span>
            </div>
          ))}
        </div>
      )}
    </AdminGuard>
  );
}

export function AutomationsPage() {
  const [data, setData] = useState(null);
  const [runs, setRuns] = useState(null);
  const [form, setForm] = useState({ name: "", trigger: "", template_code: "", action_type: "create_notification", delay: "0" });
  const load = useCallback(() => {
    apiClient.get("/admin/automation-rules").then((r) => setData(r.data)).catch(() => {});
    apiClient.get("/admin/automation-runs", { params: { page_size: 30 } }).then((r) => setRuns(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const create = async () => {
    if (!form.name || !form.trigger) return toast.error("Nom et déclencheur requis");
    const action = form.action_type === "send_email"
      ? { type: "send_email", template_code: form.template_code }
      : { type: form.action_type, title: form.name };
    try {
      await apiClient.post("/admin/automation-rules", {
        name: form.name, trigger: form.trigger, delay_minutes: parseInt(form.delay) || 0,
        actions: [action], active: true });
      toast.success("Règle créée et activée"); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const toggle = async (rule) => {
    await apiClient.patch(`/admin/automation-rules/${rule.id}`, { ...rule, active: !rule.active });
    load();
  };

  return (
    <AdminGuard>
      <h1 className="font-heading font-extrabold text-2xl text-navy mb-4">Automatisations</h1>
      {!data ? <Loader2 className="w-6 h-6 animate-spin mx-auto text-navy mt-10" /> : (
        <>
          <div className="bg-white rounded-2xl shadow-sm p-4 mb-4 flex flex-wrap gap-2 items-end" data-testid="automation-form">
            <div><label className="text-[11px] font-semibold text-navy">Nom</label>
              <Input data-testid="automation-name" value={form.name} onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))} className="h-9 bg-white rounded-lg" /></div>
            <div className="min-w-[200px]"><label className="text-[11px] font-semibold text-navy">Déclencheur</label>
              <Select value={form.trigger} onValueChange={(v) => setForm((p) => ({ ...p, trigger: v }))}>
                <SelectTrigger data-testid="automation-trigger" className="h-9 bg-white rounded-lg"><SelectValue placeholder="Choisir" /></SelectTrigger>
                <SelectContent>{data.triggers.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select></div>
            <div><label className="text-[11px] font-semibold text-navy">Action</label>
              <Select value={form.action_type} onValueChange={(v) => setForm((p) => ({ ...p, action_type: v }))}>
                <SelectTrigger data-testid="automation-action" className="h-9 w-48 bg-white rounded-lg"><SelectValue /></SelectTrigger>
                <SelectContent>{data.actions.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}</SelectContent>
              </Select></div>
            {form.action_type === "send_email" && (
              <div><label className="text-[11px] font-semibold text-navy">Code du modèle</label>
                <Input data-testid="automation-template" value={form.template_code} onChange={(e) => setForm((p) => ({ ...p, template_code: e.target.value }))} placeholder="rappel_devis" className="h-9 bg-white rounded-lg" /></div>
            )}
            <div><label className="text-[11px] font-semibold text-navy">Délai (min)</label>
              <Input data-testid="automation-delay" type="number" value={form.delay} onChange={(e) => setForm((p) => ({ ...p, delay: e.target.value }))} className="h-9 w-24 bg-white rounded-lg" /></div>
            <Button data-testid="automation-create" onClick={create} className="bg-orange hover:bg-orange/90 text-white rounded-lg h-9"><Plus className="w-4 h-4 mr-1" />Créer</Button>
          </div>
          <div className="bg-white rounded-2xl shadow-sm divide-y mb-6" data-testid="automation-rules-list">
            {data.items.length === 0 && <div className="py-8 text-center text-muted-foreground text-sm">Aucune règle — les e-mails transactionnels de base restent envoyés automatiquement.</div>}
            {data.items.map((r) => (
              <div key={r.id} className="flex items-center gap-3 px-4 py-3" data-testid={`automation-rule-${r.id}`}>
                <div className="mr-auto"><div className="text-sm font-semibold text-navy">{r.name}</div>
                  <div className="text-xs text-muted-foreground">{r.trigger} → {(r.actions || []).map((a) => a.type).join(", ")} {r.delay_minutes ? `(+${r.delay_minutes} min)` : ""}</div></div>
                <Badge>{r.active ? "Active" : "Inactive"}</Badge>
                <Button data-testid={`automation-toggle-${r.id}`} size="sm" variant="outline" className="text-xs rounded-lg h-8" onClick={() => toggle(r)}>{r.active ? "Désactiver" : "Activer"}</Button>
              </div>
            ))}
          </div>
          {runs && (
            <>
              <h2 className="font-bold text-navy mb-2">Supervision des exécutions</h2>
              <div className="flex gap-2 flex-wrap mb-3" data-testid="automation-stats">
                {Object.entries(runs.stats).map(([k, v]) => <Badge key={k}>{k} : {v}</Badge>)}
              </div>
              <div className="bg-white rounded-2xl shadow-sm divide-y" data-testid="automation-runs-list">
                {runs.items.length === 0 && <div className="py-6 text-center text-muted-foreground text-sm">Aucune exécution</div>}
                {runs.items.map((r) => (
                  <div key={r.id} className="flex items-center gap-3 px-4 py-2 text-xs">
                    <span className="font-semibold text-navy w-48 truncate">{r.rule_name}</span>
                    <span className="flex-1 truncate text-muted-foreground">{r.trigger} {r.last_error ? `— ${r.last_error}` : ""}</span>
                    <Badge>{r.status}</Badge>
                    {(r.status === "failed") && <Button size="sm" variant="ghost" className="h-7 text-xs" data-testid={`run-retry-${r.id}`}
                      onClick={async () => { await apiClient.post(`/admin/automation-runs/${r.id}/retry`); load(); }}><RotateCw className="w-3 h-3 mr-1" />Relancer</Button>}
                  </div>
                ))}
              </div>
            </>
          )}
        </>
      )}
    </AdminGuard>
  );
}

export function AuditLogPage() {
  const [data, setData] = useState(null);
  const [page, setPage] = useState(1);
  useEffect(() => {
    apiClient.get("/admin/audit-logs", { params: { page, page_size: 50 } }).then((r) => setData(r.data))
      .catch((e) => { if (e.response?.status === 403) toast.error("Accès réservé"); });
  }, [page]);
  return (
    <AdminGuard>
      <h1 className="font-heading font-extrabold text-2xl text-navy mb-4">Journal d'audit</h1>
      {!data ? <Loader2 className="w-6 h-6 animate-spin mx-auto text-navy mt-10" /> : (
        <>
          <div className="bg-white rounded-2xl shadow-sm divide-y" data-testid="audit-list">
            {data.items.map((l) => (
              <div key={l.id} className="flex flex-wrap items-center gap-3 px-4 py-2 text-xs">
                <span className="text-muted-foreground w-36">{formatDateTime(l.created_at)}</span>
                <span className="font-semibold text-navy w-44 truncate">{l.actor}</span>
                <Badge>{l.action}</Badge>
                <span className="text-muted-foreground">{l.object_type} {l.object_id ? `#${String(l.object_id).slice(-6)}` : ""}</span>
              </div>
            ))}
            {data.items.length === 0 && <div className="py-8 text-center text-muted-foreground text-sm">Journal vide</div>}
          </div>
          <div className="flex items-center gap-3 mt-3">
            <Button variant="outline" size="sm" className="rounded-lg" disabled={page <= 1} onClick={() => setPage(page - 1)}>Précédent</Button>
            <span className="text-sm">Page {page} / {data.total_pages || 1}</span>
            <Button variant="outline" size="sm" className="rounded-lg" disabled={page >= data.total_pages} onClick={() => setPage(page + 1)}>Suivant</Button>
          </div>
        </>
      )}
    </AdminGuard>
  );
}
